/** User choices for what to send to the AI model (smaller selection = smaller request). */

/** Subset of layout simulations to run before building the prompt summary. */
export type LayoutVariantGenerationOptions = {
    includeGlobalAllRoofs?: boolean;
    includeGlobalBestSubsets?: boolean;
    includeCategoryLayoutCards?: boolean;
};

export const DEFAULT_LAYOUT_VARIANT_GENERATION_OPTIONS: Required<LayoutVariantGenerationOptions> = {
    includeGlobalAllRoofs: true,
    includeGlobalBestSubsets: true,
    includeCategoryLayoutCards: true,
};

export type AiRunPayloadSelection = {
    /** Scenario indices to include; must include 0 (baseline). */
    scenarioIndices: number[];
    includeGlobalAllRoofs: boolean;
    includeGlobalBestSubsets: boolean;
    includeCategoryLayoutCards: boolean;
};

export function getDefaultAiRunPayloadSelection(scenarioCount: number): AiRunPayloadSelection {
    const n = Math.max(0, Math.floor(scenarioCount));
    return {
        scenarioIndices: Array.from({ length: n }, (_, i) => i),
        includeGlobalAllRoofs: true,
        includeGlobalBestSubsets: true,
        includeCategoryLayoutCards: true,
    };
}

export function isAiRunPayloadSelectionContinueDisabled(selection: AiRunPayloadSelection): boolean {
    return (
        !selection.includeGlobalAllRoofs &&
        !selection.includeGlobalBestSubsets &&
        !selection.includeCategoryLayoutCards
    );
}

export function applyAiRunScenarioSelection<T>(scenarios: T[], indices: number[]): T[] {
    const unique = [...new Set(indices)]
        .filter((i) => Number.isInteger(i) && i >= 0 && i < scenarios.length)
        .sort((a, b) => a - b);
    if (scenarios.length === 0) return [];
    if (!unique.includes(0)) unique.unshift(0);
    return unique.map((i) => scenarios[i]);
}

/**
 * True when the Run AI error is the payload / input token limit (see `formatGeminiApiErrorMessage` in geminiService).
 * Used to show the “Choose what to send to AI” checklist only after that failure, not on every run.
 */
export function isAiPayloadSizeExceededError(message: string | null | undefined): boolean {
    if (message == null || typeof message !== 'string') return false;
    const h = message.toLowerCase();
    return (
        h.includes('exceeded the maximum payload size') ||
        h.includes('maximum payload size allowed') ||
        h.includes('input token count exceeds') ||
        h.includes('maximum number of tokens allowed') ||
        h.includes('1048576')
    );
}

export function labelAiRunTreeScenario(s: any, index: number): string {
    if (index === 0) {
        return 'Baseline (current trees, no cuts)';
    }
    const ids = s?.treeIds?.length ? (s.treeIds as string[]) : s?.treeId != null ? [String(s.treeId)] : [];
    const label = s?.treeLabel != null && String(s.treeLabel).trim() !== '' ? String(s.treeLabel) : null;
    if (ids.length > 1) {
        return label && !label.includes(',') ? `${label} (${ids.length} trees)` : `Cut ${ids.length} trees: ${ids.join(', ')}`;
    }
    if (ids.length === 1) {
        return label ? `Cut: ${label}` : `Cut tree ${ids[0]}`;
    }
    return `Scenario ${index}`;
}
